import React, { useRef, useState } from 'react';
import { useSectionMotion, EASE } from './motion';
import Frase from './Frase';
import CapturaApp from './CapturaApp';
import OficioQueCambia from './OficioQueCambia';
import { ESTIMADOS, EQUIPO, RUTAS, IA, type Captura } from './capturas';
import { useIdioma, type Traducir } from '../../i18n/idioma';

interface Paso {
    /** Clave del diccionario: `${clave}.titulo` y `${clave}.texto`. */
    clave: string;
    /** Claves de los oficios que van rotando dentro del título. */
    oficios?: readonly string[];
    capturas?: Captura[];
    panel?: React.ReactNode;
}

const PASOS: Paso[] = [
    { clave: 'lp.producto.1', capturas: ESTIMADOS },
    { clave: 'lp.producto.2', capturas: EQUIPO },
    { clave: 'lp.producto.3', capturas: RUTAS },
    {
        clave: 'lp.producto.4',
        oficios: [
            'lp.producto.oficio.techos', 'lp.producto.oficio.pintura',
            'lp.producto.oficio.concreto', 'lp.producto.oficio.jardineria',
            'lp.producto.oficio.remodelacion',
        ],
        capturas: IA,
    },
];

/** Las pantallas de un paso, o su maqueta si todavía no hay capturas. */
function vistasDe(paso: Paso): React.ReactNode[] {
    if (!paso.capturas) return [paso.panel];
    return paso.capturas.map((c) => <CapturaApp key={c.src} captura={c} />);
}

/**
 * El fold del producto: la app se demuestra sola mientras bajas.
 *
 * HISTORIA DE COLOR, para que nadie la repita: este fold estuvo bañado en
 * #FACC15 de borde a borde. La idea era buena sobre el papel —tener el amarillo
 * de marca y usarlo solo al 13 % detrás de unos íconos es no comprometerse con
 * él—, pero al equipo le pareció un grito y lo señaló junto al resto de la
 * mitad de arriba. Ahora la superficie es blanca y el amarillo vive donde de
 * verdad trabaja: los botones y los acentos DENTRO de las maquetas. Si alguien
 * quiere recuperar el baño, es cambiar una clase — pero que sea una decisión,
 * no un descuido.
 *
 * El panel de la derecha se queda pegado con `position: sticky` en vez de con
 * el pin de ScrollTrigger: sticky lo resuelve el compositor del navegador, no
 * hay recálculo de alturas ni el salto de un espaciador. GSAP se encarga solo
 * de lo que sticky no puede — saber qué paso está activo y firmar la firma.
 */
const Producto: React.FC = () => {
    const { t } = useIdioma();
    /* Qué se está viendo: qué paso y, dentro de él, qué pantalla. */
    const [vista, setVista] = useState({ paso: 0, img: 0 });

    /* El disparador avisa en CADA cuadro del scroll. Sin este filtro, React
       volvería a pintar el fold sesenta veces por segundo para dejarlo igual. */
    const ultima = useRef('0-0');
    const poner = (paso: number, img: number) => {
        const clave = `${paso}-${img}`;
        if (ultima.current === clave) return;
        ultima.current = clave;
        setVista({ paso, img });
    };

    const scope = useSectionMotion<HTMLElement>(({ gsap, scope, motion, always, reveal, titular }) => {
        /* `always`: qué paso está activo es INFORMACIÓN, no decoración. Tiene
           que seguir funcionando con movimiento reducido — lo que cambia es
           que el relevo entre pantallas ya no se funde (lo apaga el CSS). */
        always(() => {
            scope.querySelectorAll('[data-paso]').forEach((el, i) => {
                const cuantas = vistasDe(PASOS[i]).length;
                gsap.timeline({
                    scrollTrigger: {
                        trigger: el,
                        start: 'top 55%',
                        end: 'bottom 55%',
                        onEnter: () => poner(i, 0),
                        onEnterBack: () => poner(i, cuantas - 1),
                        /* Y aquí está el segundo nivel: el paso ya cambia entre
                           los cuatro temas, y ADEMÁS dentro de cada tema van
                           pasando sus propias pantallas conforme lees su
                           párrafo. El bloque de texto se reparte en tantos
                           tramos como capturas tenga. */
                        onUpdate: (self) => {
                            poner(i, Math.min(cuantas - 1, Math.floor(self.progress * cuantas)));
                        },
                    },
                });
            });
        });

        titular({ start: 'top 72%' });

        reveal('[data-fade]', { start: 'top 66%' });

        /* Aquí se dibujaba sola la firma del trazo `[data-firma]`, con
           dasharray. Ese atributo solo existe en `AppPanels.tsx`, que dejó de
           renderizarse cuando la maqueta pasó a ser capturas de pantalla: el
           `forEach` recorría una lista vacía y no animaba nada. Se retira el
           código muerto; el archivo huérfano sigue en su sitio, que borrarlo
           es decisión de una persona. */

        motion(() => {
            /* La maqueta pegada respira mientras se releva: entra desde un poco
               más abajo y con un punto de escala. Solo con movimiento pleno. */
            gsap.from(scope.querySelector('[data-marco-maqueta]'), {
                y: 52,
                scale: 0.975,
                autoAlpha: 0,
                duration: 1.4,
                ease: EASE.out,
                scrollTrigger: { trigger: scope, start: 'top 60%' },
            });
        });
    });

    return (
        <section
            ref={scope}
            id="producto"
            /* Fondo blanco, no el baño de amarillo que tenía.
               El amarillo a fold completo era mi apuesta de color y el equipo lo
               metió en el mismo saco de lo «espantoso» de la parte de arriba. El
               lenguaje que sí aprobaron es callado: superficie clara, tipografía
               navy grande y aire. Aquí eso además ayuda — las maquetas de la app
               son navy oscuro y sobre blanco tienen más presencia y menos ruido
               que sobre amarillo. El amarillo se queda donde manda: los botones
               y los acentos dentro del producto. */
            className="rp-on-light bg-white px-5 py-[clamp(5rem,11vw,9rem)] sm:px-8"
        >
            <div className="mx-auto max-w-[1240px]">
                <h2 className="rp-h2 max-w-[12ch] text-navy-950">
                    <Frase marca="titular">{t('lp.producto.titular')}</Frase>
                </h2>
                <p data-fade className="rp-lead mt-6 text-acero-700">
                    {t('lp.producto.lead')}
                </p>

                {/* ── Móvil y tablet: cada paso lleva su pantalla debajo. ──
                    Solo la PRIMERA de cada paso. El relevo de aquí abajo
                    depende de un marco pegado que en el teléfono no existe, y
                    apilar las nueve dejaría un fold interminable en la pantalla
                    donde menos paciencia hay. */}
                <div className="mt-14 space-y-16 lg:hidden">
                    {PASOS.map((paso) => (
                        <div key={paso.clave}>
                            <h3 className="rp-h3 text-navy-950">
                                {t(`${paso.clave}.titulo` as Parameters<Traducir>[0])}
                                {paso.oficios && (
                                    <>
                                        {' '}
                                        <OficioQueCambia palabras={paso.oficios.map((c) => t(c as Parameters<Traducir>[0]))} />
                                    </>
                                )}
                            </h3>
                            <p className="rp-body mt-3 text-acero-700">{t(`${paso.clave}.texto` as Parameters<Traducir>[0])}</p>
                            <div className="mt-7">{vistasDe(paso)[0]}</div>
                        </div>
                    ))}
                </div>

                {/* ── Escritorio: columna de pasos + maqueta pegada. ── */}
                <div className="mt-20 hidden gap-16 lg:grid lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        {PASOS.map((paso, i) => (
                            <div
                                key={paso.clave}
                                data-paso
                                /* justify-start y no justify-center: centrado, el
                                   primer paso caía 300 px por debajo del borde
                                   superior de la maqueta pegada y el fold abría
                                   con un hueco enorme. Alineados arriba, texto y
                                   maqueta arrancan a la misma altura.
                                   El último mide menos: si midiera lo mismo, el
                                   fold cerraría con media pantalla vacía después
                                   de la última maqueta. */
                                className="flex min-h-[62vh] flex-col justify-start last:min-h-[42vh]"
                            >
                                <h3 className="rp-h3 inline-block text-navy-950">
                                    {t(`${paso.clave}.titulo` as Parameters<Traducir>[0])}
                                    {paso.oficios && (
                                        <>
                                            {' '}
                                            <OficioQueCambia palabras={paso.oficios.map((c) => t(c as Parameters<Traducir>[0]))} />
                                        </>
                                    )}
                                    {/* Subrayado que se dibuja en el paso activo.
                                        Un subrayado, no una barra lateral de color:
                                        esa es la muletilla que se eliminó de toda
                                        la app en el rediseño. */}
                                    <span
                                        aria-hidden="true"
                                        className={`rp-underline mt-2.5 block h-[2px] bg-navy-850 ${
                                            i === vista.paso ? 'scale-x-100' : 'scale-x-0'
                                        }`}
                                    />
                                </h3>
                                <p className="rp-body mt-5 max-w-[42ch] text-acero-700">
                                    {t(`${paso.clave}.texto` as Parameters<Traducir>[0])}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="lg:col-span-6 lg:col-start-7">
                        <div className="sticky top-28">
                            {/* La caja mide exactamente lo que miden las capturas. Antes llevaba
                                `min-h-[470px]` para que cupiera la maqueta de IA, que era más
                                alta; ahora las cuatro pantallas son capturas de la misma
                                proporción y el marco se ajusta solo. */}
                            <div data-marco-maqueta className="relative aspect-[1640/936]">
                                {PASOS.flatMap((paso, i) =>
                                    vistasDe(paso).map((vistaPaso, j) => {
                                        const puesta = i === vista.paso && j === vista.img;
                                        return (
                                            <div
                                                key={`${paso.clave}-${j}`}
                                                aria-hidden={!puesta}
                                                /* `items-center`: las capturas miden
                                                   343 px de alto en esta columna y el
                                                   marco 470, porque la maqueta de IA
                                                   que queda es más alta. Centradas, el
                                                   relevo no da tirones verticales. */
                                                className={`rp-swap absolute inset-0 flex items-center ${
                                                    puesta
                                                        ? 'opacity-100'
                                                        : 'pointer-events-none opacity-0'
                                                }`}
                                            >
                                                <div className="w-full">{vistaPaso}</div>
                                            </div>
                                        );
                                    }),
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Producto;
